import tokens from '@taller/tokens/tokens.json';
import type { CSSProperties } from 'react';
import { cn } from '@/ui/cn';

/**
 * Fixed Tallerza brand colors for the logo. The workshop may repaint the
 * interface with its own colors (lib/temaTaller.ts overrides --tm-acento and
 * the action tokens), but the Tallerza logo never changes: it reads these
 * constants from the platform tokens, switching only with the theme.
 */
const MARCA = {
  '--logo-acento': tokens.color.claro.acento,
  '--logo-tinta': tokens.color.claro.text,
  '--logo-acento-oscuro': tokens.color.oscuro.acento,
  '--logo-tinta-oscuro': tokens.color.oscuro.text,
} as CSSProperties;
const ACENTO = 'fill-(--logo-acento) dark:fill-(--logo-acento-oscuro)';
const TINTA = 'fill-(--logo-tinta) dark:fill-(--logo-tinta-oscuro)';

/**
 * Tallerza cell icon (brand handoff, docs/landing/design_handoff_tallerza_landing):
 * viewBox 30×30, five 9×9 cells with radius 2 and 1.5 gap. The top row and the
 * upper trunk cell take the brand accent, the bottom cell the ink
 * (#E11D74/#1A1718 light, #FF5FA2/#F6F2EC dark), fixed whatever the workshop
 * colors. Below 24 px the handoff switches to the favicon construction (a
 * solid accent tile with a white T).
 */
export function IconoTallerza({ size = 30, className }: { size?: number; className?: string }) {
  if (size < 24) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 16 16"
        className={className}
        style={MARCA}
        aria-hidden="true"
      >
        <rect width="16" height="16" rx="3.5" className={ACENTO} />
        <rect x="3" y="3" width="10" height="3" fill="#FFFFFF" />
        <rect x="6.5" y="3" width="3" height="10" fill="#FFFFFF" />
      </svg>
    );
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 30 30"
      className={className}
      style={MARCA}
      aria-hidden="true"
    >
      {CELDAS.map(([x, y]) => (
        <rect key={`${x}-${y}`} x={x} y={y} width="9" height="9" rx="2" className={ACENTO} />
      ))}
      <rect x="10.5" y="21" width="9" height="9" rx="2" className={TINTA} />
    </svg>
  );
}

const CELDAS = [
  [0, 0],
  [10.5, 0],
  [21, 0],
  [10.5, 10.5],
] as const;

const tamanos = {
  xs: { icono: 16, texto: 'text-[15px]', gap: 'gap-1.5' },
  sm: { icono: 24, texto: 'text-[21px]', gap: 'gap-2' },
  md: { icono: 26, texto: 'text-[23px]', gap: 'gap-2.5' },
  lg: { icono: 34, texto: 'text-[30px]', gap: 'gap-3' },
} as const;

/**
 * Horizontal Tallerza logo: cell icon + "tallerza" wordmark in Sora 700
 * (tracking -0.04em). The wordmark is live text in the ink token, so it
 * passes AA in both themes and needs no logotype exemption. The DOM text is
 * "Tallerza" (what assistive tech reads); the lowercase is only visual.
 * Never shows a workshop's brand: that lives in the workshop block (top
 * left); in the workshop space this logo goes small at the top right.
 */
export function LogoTallerza({
  className,
  tamano = 'md',
}: {
  className?: string;
  tamano?: keyof typeof tamanos;
}) {
  const t = tamanos[tamano];
  return (
    <span className={cn('inline-flex items-center', t.gap, className)}>
      <IconoTallerza size={t.icono} />
      <span
        className={cn(
          'font-titulo leading-none font-bold tracking-[-0.04em] text-text lowercase',
          t.texto,
        )}
      >
        Tallerza
      </span>
    </span>
  );
}
